import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Alert, FlatList, Platform, RefreshControl, TextInput, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'react-native-toast-notifications';
import { Ionicons } from '@expo/vector-icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import { Icon } from '../../../components/Icon/Icon';
import api from '../../../utils/api';

import GuardAccessAuthorizationDetailModal from './Access/GuardAccessAuthorizationDetailModal';
import GuardNewAccessAuthorizationModal from './Access/GuardNewAccessAuthorizationModal';
import { useAccessAuthorizationConfig } from './Access/accessAuthorizationConfig';

dayjs.locale('es');


type SubTab = 'inside' | 'authorizations' | 'packages';


export default function GuardAccessScreen({ route }: any) {
  const { communityId } = route.params ?? {};
  const [selectedTab, setSubTab] = useState<SubTab>('authorizations');
  const [shown, setShown] = useState<any>(null);
  const [showNew, setShowNew] = useState(false);

  const [search, setSearch] = useState('');
  const toast = useToast();
  const { guardCanCreate, enabled: authorizationsEnabled, boothDelivery } = useAccessAuthorizationConfig(communityId);

  // No todos los fraccionamientos usan autorizaciones o reparto en caseta: solo se muestran
  // las pestañas activas. Dentro siempre: es para todo el que entra (una autorización con
  // entrada registrada se vuelve uno más dentro).
  const availableTabs: SubTab[] = [
    'inside',
    ...(authorizationsEnabled ? ['authorizations'] as SubTab[] : []),
    ...(boothDelivery ? ['packages'] as SubTab[] : []),
  ];
  const subTab: SubTab = availableTabs.includes(selectedTab) ? selectedTab : availableTabs[0];

  const { data: authorizations, isLoading, refetch } = useQuery<any[]>({
    queryKey: ['guard.access-authorizations.today', communityId],
    queryFn: () =>
      api.get(`/communities/${communityId}/guard/access-authorizations/today`).then(r => r.data),
    enabled: !!communityId && authorizationsEnabled,
  });

  // Proveedores con responsiva que siguen adentro (puede incluir de días anteriores)
  const insideQuery = useQuery<any[]>({
    queryKey: ['guard.access-authorizations.inside', communityId],
    queryFn: () =>
      api.get(`/communities/${communityId}/guard/access-authorizations/inside`).then(r => r.data),
    enabled: !!communityId,
  });
  const insideItems = insideQuery.data ?? [];

  // Sin QR el vigilante encuentra la responsiva buscando: proveedor, vivienda, placas o folio
  const items = useMemo(() => {
    const all = authorizations ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;

    return all.filter((a: any) => [
      a.provider_name, a.service_type, a.description, a.unit?.name, a.folio, a.requester?.name, a.vehicle_info?.plate_number,
    ].some(v => (v ?? '').toString().toLowerCase().includes(q)));
  }, [authorizations, search]);

  const counts = {
    inside: insideItems.length,
    authorizations: authorizations?.length ?? 0,
    packages: 0,
  };

  // La pestaña abierta no tiene nada que listar (la card se encoge).
  const contentIsEmpty =
    subTab === 'authorizations' ? (!isLoading && items.length === 0) :
    subTab === 'inside' ? (!insideQuery.isLoading && insideItems.length === 0) :
    true;

  function refetchAll() {
    refetch();
    insideQuery.refetch();
  }

  // Con confirmación: marcar una salida que no pasó es peor que olvidar registrarla
  function quickExit(auth: any) {
    const name = auth.provider_name || auth.unit?.name;

    if (Platform.OS === 'web') {
      if (window.confirm(`¿Registrar la salida de ${name}?`)) doExit(auth);
      return;
    }

    Alert.alert('Registrar salida', `¿Registrar la salida de ${name}?`, [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Registrar', onPress: () => doExit(auth) },
    ]);
  }

  async function doExit(auth: any) {
    try {
      await api.post(`/communities/${communityId}/access-authorizations/${auth.uuid}/exit`);
      toast.show('Salida registrada', { type: 'success' });
      refetchAll();
    } catch (err: any) {
      toast.show(err?.response?.data?.message ?? 'No se pudo registrar la salida', { type: 'warning' });
    }
  }

  return (
    <View style={tw`flex-1 bg-background`}>
      <SafeAreaView style={tw`flex-1`} edges={['top']}>
        {/* Header */}
        <View style={tw`px-4 pt-4 pb-2`}>
          <View style={tw`row items-center self-center bg-white border border-gray-200 rounded-full px-4 py-1.5`}>
            <Icon name='calendario' size={14} color={tw.color('primary')} />
            <Text style={tw`ml-2 text-primary font-500`}>
              {dayjs().format('ddd D MMM | HH:mm')}
            </Text>
          </View>
        </View>

        {/* Sub-tabs como cards */}
        <View style={tw`row gap-2 px-4 mt-2`}>
          <SubTabCard label='Dentro' count={counts.inside} active={subTab === 'inside'} onPress={() => setSubTab('inside')} />
          {authorizationsEnabled && (
            <SubTabCard label='Autorizaciones' count={counts.authorizations} active={subTab === 'authorizations'} onPress={() => setSubTab('authorizations')} />
          )}
          {boothDelivery && (
            <SubTabCard label='Paquetes' count={counts.packages} active={subTab === 'packages'} onPress={() => setSubTab('packages')} accent='packages' />
          )}
        </View>

        {/* Contenido */}
        {/* Con elementos ocupa el alto disponible; sin nada se queda chica para no llegar al
            botón flotante (igual que Incidencias). */}
        <View style={tw.style(
          `mt-4 mx-4 bg-white border border-gray-200 rounded-2xl overflow-hidden`,
          !contentIsEmpty && `flex-1 mb-4`,
        )}>
          {subTab === 'authorizations' && (
            <>
              <View style={tw`row items-center border-b border-gray-100 px-4 py-2`}>
                <Ionicons name='search' size={18} color={tw.color('gray-400')} />
                <TextInput
                  value={search}
                  onChangeText={setSearch}
                  placeholder='Buscar proveedor, vivienda, placas o folio'
                  placeholderTextColor={tw.color('gray-400')}
                  autoCorrect={false}
                  style={tw`flex-1 ml-2 py-2 text-base`}
                />
                {!!search && (
                  <TouchableOpacity onPress={() => setSearch('')}>
                    <Ionicons name='close-circle' size={18} color={tw.color('gray-400')} />
                  </TouchableOpacity>
                )}
              </View>

              {isLoading && <View style={tw`p-10 items-center`}><ActivityIndicator /></View>}

              {!isLoading && items.length === 0 && (
                <View style={tw`items-center justify-center p-10`}>
                  <Text style={tw`text-gray-400 text-center`}>
                    {search ? 'Ninguna autorización coincide con la búsqueda' : 'No hay autorizaciones vigentes para hoy'}
                  </Text>
                </View>
              )}

              {items.length > 0 && <FlatList
                data={items}
                keyExtractor={(item: any) => item.uuid}
                renderItem={({ item }) => (
                  <AuthorizationRow auth={item} onPress={() => setShown(item)} />
                )}
                refreshControl={<RefreshControl onRefresh={refetchAll} refreshing={isLoading} />}
                ListFooterComponent={
                  items.length > 0 ? (
                    <TouchableOpacity style={tw`py-4 items-center`}>
                      <Text style={tw`text-primary font-500`}>Ver autorizaciones cumplidas</Text>
                    </TouchableOpacity>
                  ) : null
                }
                // Espacio para que el botón flotante no tape el último renglón.
                contentContainerStyle={tw`pb-20`}
              />}
            </>
          )}

          {subTab === 'inside' && (
            <>
              {insideQuery.isLoading && <View style={tw`p-10 items-center`}><ActivityIndicator /></View>}

              {!insideQuery.isLoading && insideItems.length === 0 && (
                <View style={tw`items-center justify-center p-10`}>
                  <Text style={tw`text-gray-400 text-center`}>No hay proveedores adentro</Text>
                </View>
              )}

              {insideItems.length > 0 && <FlatList
                data={insideItems}
                keyExtractor={(item: any) => item.uuid}
                renderItem={({ item }) => (
                  <InsideRow auth={item} onPress={() => setShown(item)} onExit={() => quickExit(item)} />
                )}
                refreshControl={<RefreshControl onRefresh={refetchAll} refreshing={insideQuery.isLoading} />}
              />}
            </>
          )}

          {subTab === 'packages' && (
            <View style={tw`items-center justify-center p-10`}>
              <Text style={tw`text-gray-400 text-center`}>Próximamente</Text>
            </View>
          )}
        </View>

        {/* Botón flotante */}
        {subTab === 'authorizations' && guardCanCreate && (
          <TouchableOpacity
            onPress={() => setShowNew(true)}
            style={tw`absolute bottom-6 right-6 bg-primary px-5 py-3 rounded-full row items-center shadow-md`}
          >
            <Icon name='add' size={16} color='white' />
            <Text style={tw`text-white font-500 ml-2`}>Nueva autorización</Text>
          </TouchableOpacity>
        )}
      </SafeAreaView>

      <GuardAccessAuthorizationDetailModal
        visible={!!shown}
        authorization={shown}
        communityId={communityId}
        onRequestClose={() => setShown(null)}
      />

      <GuardNewAccessAuthorizationModal
        visible={showNew}
        communityId={communityId}
        onRequestClose={() => setShowNew(false)}
        onCreated={() => {
          setShowNew(false);
          refetchAll();
        }}
      />
    </View>
  );
}


// Seleccionada: turquesa por defecto; Paquetes lleva su propio morado (accent="packages").
const SUB_TAB_ACCENTS = {
  default: { border: `border-primary`, bg: `bg-primary/5`, text: `text-primary` },
  packages: { border: `border-secondary-400`, bg: `bg-[#F9F0FF]`, text: `text-secondary-400` },
};

function SubTabCard({ label, count, active, onPress, accent = 'default' }: any) {
  const colors = SUB_TAB_ACCENTS[accent as keyof typeof SUB_TAB_ACCENTS] ?? SUB_TAB_ACCENTS.default;

  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(
        `flex-1 bg-white border border-gray-200 rounded-xl py-3 items-center`,
        active && `${colors.border} ${colors.bg}`,
      )}
    >
      <Text style={tw.style(`text-2xl font-600 text-gray-500`, active && colors.text)}>{count}</Text>
      <Text style={tw.style(`text-sm text-gray-500`, active && `${colors.text} font-500`)}>{label}</Text>
    </TouchableOpacity>
  );
}


/** Tipo del catálogo y descripción, lo que haya. */
function serviceSummary(auth: any) {
  return [auth.service_type, auth.description].filter(Boolean).join(' · ');
}


function AuthorizationRow({ auth, onPress }: any) {
  return (
    <TouchableOpacity onPress={onPress} style={tw.style(`row justify-between items-center border-b border-gray-100 px-4 py-3`, auth.status === 'suspended' && `opacity-60`)}>
      <View style={tw`flex-1 pr-3`}>
        <View style={tw`row items-center mb-1`}>
          <Text style={tw`text-primary font-600`}>{auth.unit?.name}</Text>
          <Text style={tw`text-gray-400 mx-2`}>·</Text>
          <Text style={tw`text-gray-600 flex-shrink`} numberOfLines={1}>{auth.provider_name || auth.requester?.name}</Text>
          {auth.status === 'suspended'
            ? <Badge label='Suspendida' style='bg-red-100' textStyle='text-red-700' />
            : <TodayAccessBadge auth={auth} />}
        </View>
        <Text style={tw`text-gray-500`} numberOfLines={1}>{serviceSummary(auth)}</Text>
        <View style={tw`row mt-1`}>
          <Text style={tw`text-gray-400 text-sm mr-3`}>{formatTime(auth)}</Text>
          <Text style={tw`text-gray-400 text-sm`}>{auth.access_type_str}</Text>
        </View>
      </View>

      <Text style={tw`text-gray-400 text-sm`}>{auth.start_time ? auth.start_time.slice(0, 5) : 'Todo el día'}</Text>
    </TouchableOpacity>
  );
}


function InsideRow({ auth, onPress, onExit }: any) {
  const since = auth.last_entry_log?.entry_time;
  const sinceStr = since
    ? (dayjs(since).isSame(dayjs(), 'day') ? dayjs(since).format('HH:mm') : dayjs(since).format('D MMM HH:mm'))
    : null;

  return (
    <TouchableOpacity onPress={onPress} style={tw`row justify-between items-center border-b border-gray-100 px-4 py-3`}>
      <View style={tw`flex-1 pr-3`}>
        <View style={tw`row items-center mb-1`}>
          <Text style={tw`text-primary font-600`}>{auth.unit?.name}</Text>
          <Text style={tw`text-gray-400 mx-2`}>·</Text>
          <Text style={tw`text-gray-600 flex-shrink`} numberOfLines={1}>{auth.provider_name || auth.requester?.name}</Text>
        </View>
        <Text style={tw`text-gray-500`} numberOfLines={1}>{serviceSummary(auth)}</Text>
        {sinceStr && <Text style={tw`text-gray-400 text-sm mt-1`}>Entró {sinceStr}</Text>}
      </View>

      <TouchableOpacity onPress={onExit} style={tw`border border-secondary rounded-full px-3 py-1.5`}>
        <Text style={tw`text-secondary font-500`}>Salida</Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );
}


/** Estado del día: no ha llegado, está adentro o ya salió (puede volver a entrar). */
function TodayAccessBadge({ auth }: any) {
  const last = auth.last_entry_log;

  if (auth.is_inside) return <Badge label='Adentro' style='bg-green-100' textStyle='text-green-700' />;
  if (last?.exit_time && dayjs(last.exit_time).isSame(dayjs(), 'day')) {
    return <Badge label={`Salió ${dayjs(last.exit_time).format('HH:mm')}`} style='bg-blue-50' textStyle='text-blue-700' />;
  }
  return <Badge label='En espera' style='bg-gray-100' textStyle='text-gray-500' />;
}


function Badge({ label, style, textStyle }: { label: string; style: string; textStyle: string }) {
  return (
    <View style={tw.style(`ml-2 px-2 py-0.5 rounded-full`, style)}>
      <Text style={tw.style(`text-xs font-500`, textStyle)}>{label}</Text>
    </View>
  );
}


function formatTime(auth: any) {
  if (!auth.start_time) return 'Todo el día';
  const start = auth.start_time.slice(0, 5);
  const end = auth.end_time ? auth.end_time.slice(0, 5) : null;
  return end ? `${start} - ${end}` : `Desde ${start}`;
}
